import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Users, Search, Trash2, Star, UserCog, X, GraduationCap, User, Check, CreditCard, MessageCircle, ChevronLeft, ChevronRight, Phone, UserPlus } from "lucide-react";
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Avatar, AvatarImage, AvatarFallback } from "../../components/ui/avatar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from "../../components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../../components/ui/dropdown-menu";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { API } from "../../App";
import { RANKING_COLORS, RANKING_LEVELS } from "./constants";
import ProfessorFeedback from "../professor/ProfessorFeedback";

const LIMIT = 15;

const AdminStudents = () => {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [filterType, setFilterType] = useState("all");

  const [selectedStudent, setSelectedStudent] = useState(null);
  const [creditAction, setCreditAction] = useState("add");
  const [creditAmount, setCreditAmount] = useState(5);
  const [creditReason, setCreditReason] = useState("Pack de aulas");
  const [creditType, setCreditType] = useState("credits");
  const [isCreditsDialogOpen, setIsCreditsDialogOpen] = useState(false);
  const [isRankingDialogOpen, setIsRankingDialogOpen] = useState(false);
  const [isFeedbackDialogOpen, setIsFeedbackDialogOpen] = useState(false);
  const [rankingSurf, setRankingSurf] = useState("");
  const [rankingBodyboard, setRankingBodyboard] = useState("");
  const [subscriptionMap, setSubscriptionMap] = useState({});
  const [showCreateStudent, setShowCreateStudent] = useState(false);
  const [newStudent, setNewStudent] = useState({ name: "", email: "", phone: "" });

  const debounceRef = useRef(null);

  const fetchStudents = useCallback(async (p, q, f) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: p, limit: LIMIT });
      if (q) params.set("search", q);
      if (f && f !== "all") params.set("filter", f);
      const [studentsRes, subsRes] = await Promise.all([
        axios.get(`${API}/admin/students?${params}`, { withCredentials: true }),
        axios.get(`${API}/subscriptions`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      const data = studentsRes.data;
      setStudents(data.students);
      setTotal(data.total);
      setPages(data.pages);
      setPage(data.page);
      const sMap = {};
      subsRes.data.forEach(s => { sMap[s.user_id] = s; });
      setSubscriptionMap(sMap);
    } catch (err) {
      console.error("Error fetching students:", err);
      toast.error("Erro ao carregar alunos");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStudents(1, "", "all");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run once on mount - fetchStudents is stable but ESLint can't verify

  const handleSearchChange = (value) => {
    setSearchInput(value);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setSearchQuery(value);
      setPage(1);
      fetchStudents(1, value, filterType);
    }, 350);
  };

  const handleFilterChange = (f) => {
    setFilterType(f);
    setPage(1);
    fetchStudents(1, searchQuery, f);
  };

  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > pages) return;
    fetchStudents(newPage, searchQuery, filterType);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const refreshCurrentPage = () => fetchStudents(page, searchQuery, filterType);

  const handleUpdateCredits = async () => {
    try {
      await axios.patch(
        `${API}/admin/users/${selectedStudent.user_id}/credits`,
        { action: creditAction, amount: creditAmount, reason: creditReason, credit_type: creditType },
        { withCredentials: true }
      );
      toast.success("Creditos atualizados!");
      setIsCreditsDialogOpen(false);
      refreshCurrentPage();
    } catch {
      toast.error("Erro ao atualizar creditos");
    }
  };

  const handleChangeRole = async (userId, newRole) => {
    try {
      await axios.patch(`${API}/admin/users/${userId}/role`, { role: newRole }, { withCredentials: true });
      toast.success(`Role alterado para ${newRole}`);
      refreshCurrentPage();
    } catch {
      toast.error("Erro ao alterar role");
    }
  };

  const handleBlockToggle = async (student) => {
    const blocked = !student.blocked;
    try {
      await axios.patch(`${API}/admin/users/${student.user_id}/block`, { blocked }, { withCredentials: true });
      toast.success(blocked ? "Aluno bloqueado" : "Aluno desbloqueado");
      refreshCurrentPage();
    } catch {
      toast.error("Erro ao alterar estado do aluno");
    }
  };

  const handleRemoveUser = async (student) => {
    if (!window.confirm(`Tens a certeza que queres remover ${student.name}? Esta acao e irreversivel.`)) return;
    try {
      await axios.delete(`${API}/admin/users/${student.user_id}`, { withCredentials: true });
      toast.success("Aluno removido");
      refreshCurrentPage();
    } catch {
      toast.error("Erro ao remover aluno");
    }
  };

  const openCreditsDialog = (student) => {
    setSelectedStudent(student);
    setCreditAction("add");
    setCreditAmount(5);
    setCreditReason("Pack de aulas");
    setCreditType("credits");
    setIsCreditsDialogOpen(true);
  };

  const openRankingDialog = (student) => {
    setSelectedStudent(student);
    setRankingSurf(student.ranking_surf || "");
    setRankingBodyboard(student.ranking_bodyboard || "");
    setIsRankingDialogOpen(true);
  };

  const openFeedbackDialog = (student) => {
    setSelectedStudent(student);
    setIsFeedbackDialogOpen(true);
  };

  const handleUpdateRanking = async (modality, ranking) => {
    try {
      await axios.patch(
        `${API}/staff/users/${selectedStudent.user_id}/ranking`,
        { modality, ranking: ranking || null },
        { withCredentials: true }
      );
      toast.success(`Ranking de ${modality} atualizado!`);
      refreshCurrentPage();
      if (modality === "surf") setRankingSurf(ranking);
      else setRankingBodyboard(ranking);
    } catch {
      toast.error("Erro ao atualizar ranking");
    }
  };

  const handleCreateStudent = async () => {
    if (!newStudent.name.trim()) { toast.error("Nome é obrigatório"); return; }
    try {
      const res = await axios.post(`${API}/admin/students`, newStudent, { withCredentials: true });
      toast.success(`Aluno "${res.data.name}" criado`);
      setShowCreateStudent(false);
      setNewStudent({ name: "", email: "", phone: "" });
      refreshCurrentPage();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao criar aluno"); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-[#3d5a63] mb-1">Alunos</h1>
          <p className="text-sm text-[#475569]">Lista de alunos registados e gestao de packs</p>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" onClick={() => setShowCreateStudent(true)} className="bg-[#6697a4] hover:bg-[#567f8a]" data-testid="create-student-btn">
            <UserPlus className="h-4 w-4 mr-1" /> Novo Aluno
          </Button>
          <Badge variant="outline" className="text-[#6697a4]">{total} aluno{total !== 1 ? "s" : ""}</Badge>
        </div>
      </div>

      {/* Search & Filter */}
      <div className="flex flex-col gap-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#94A3B8]" />
          <Input
            placeholder="Pesquisar por nome, email ou telefone..."
            value={searchInput}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="pl-10"
            data-testid="student-search"
          />
          {searchInput && (
            <button onClick={() => { setSearchInput(""); handleSearchChange(""); }} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#94A3B8] hover:text-[#475569]">
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        <div className="flex gap-2 overflow-x-auto pb-1">
          {[
            { value: "all", label: "Todos" },
            { value: "subscription", label: "Mensalistas" },
            { value: "credits", label: "Com Creditos" },
            { value: "no_credits", label: "Sem Creditos" },
          ].map(f => (
            <Button
              key={f.value}
              variant={filterType === f.value ? "default" : "outline"}
              size="sm"
              className={`whitespace-nowrap rounded-full ${filterType === f.value ? "bg-[#6697a4] text-white" : ""}`}
              onClick={() => handleFilterChange(f.value)}
              data-testid={`filter-${f.value}`}
            >{f.label}</Button>
          ))}
        </div>
      </div>

      {/* Credits Dialog */}
      <Dialog open={isCreditsDialogOpen} onOpenChange={setIsCreditsDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Gerir Creditos - {selectedStudent?.name}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="text-center p-4 bg-slate-50 rounded-xl">
                <p className="text-sm text-[#94A3B8]">Creditos Grupo</p>
                <p className="text-3xl font-bold text-[#6697a4]">{selectedStudent?.credits || 0}</p>
              </div>
              <div className="text-center p-4 bg-slate-50 rounded-xl">
                <p className="text-sm text-[#94A3B8]">Creditos Privados</p>
                <p className="text-3xl font-bold text-[#e8927c]">{selectedStudent?.private_credits || 0}</p>
              </div>
            </div>
            <div><Label>Tipo de Credito</Label>
              <Select value={creditType} onValueChange={setCreditType}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="credits">Grupo</SelectItem><SelectItem value="private_credits">Privado</SelectItem></SelectContent></Select>
            </div>
            <div><Label>Acao</Label>
              <Select value={creditAction} onValueChange={setCreditAction}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="add">Adicionar Creditos</SelectItem><SelectItem value="subtract">Remover Creditos</SelectItem><SelectItem value="set">Definir Valor</SelectItem></SelectContent></Select>
            </div>
            <div><Label>Quantidade</Label><Input type="number" value={creditAmount} onChange={(e) => setCreditAmount(e.target.value === '' ? '' : parseInt(e.target.value))} onBlur={(e) => setCreditAmount(parseInt(e.target.value) || 0)} min={0} /></div>
            <div><Label>Motivo</Label>
              <Select value={creditReason} onValueChange={setCreditReason}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="Pack de aulas">Pack de aulas</SelectItem><SelectItem value="Pack Familia">Pack Familia</SelectItem><SelectItem value="Mensalidade">Mensalidade</SelectItem><SelectItem value="Ajuste manual">Ajuste manual</SelectItem><SelectItem value="Oferta promocional">Oferta promocional</SelectItem></SelectContent></Select>
            </div>
          </div>
          <DialogFooter>
            <DialogClose asChild><Button variant="outline">Cancelar</Button></DialogClose>
            <Button onClick={handleUpdateCredits} className="bg-[#6697a4] hover:bg-[#3d5a63]">Confirmar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Ranking Dialog */}
      <Dialog open={isRankingDialogOpen} onOpenChange={setIsRankingDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Rankings - {selectedStudent?.name}</DialogTitle></DialogHeader>
          <div className="space-y-6">
            {["surf", "bodyboard"].map(mod => {
              const current = mod === "surf" ? rankingSurf : rankingBodyboard;
              return (
                <div key={mod}>
                  <Label className="text-base font-semibold text-[#3d5a63] mb-3 block capitalize">{mod === "surf" ? "Surf" : "Bodyboard"}</Label>
                  <div className="flex flex-wrap gap-2">
                    <Button size="sm" variant={!current ? "default" : "outline"} className={!current ? "bg-[#94A3B8]" : ""} onClick={() => handleUpdateRanking(mod, "")} data-testid={`ranking-${mod}-none`}>Sem ranking</Button>
                    {RANKING_LEVELS.map(level => (
                      <Button key={level} size="sm" variant={current === level ? "default" : "outline"} className={current === level ? RANKING_COLORS[level]?.replace("border-", "ring-1 ring-") : ""} onClick={() => handleUpdateRanking(mod, level)} data-testid={`ranking-${mod}-${level.toLowerCase()}`}>
                        <span className={`w-3 h-3 rounded-full mr-1.5 border ${RANKING_COLORS[level]}`}></span>{level}
                      </Button>
                    ))}
                  </div>
                  {current && <p className="text-sm text-[#6697a4] mt-2">Nivel atual: <strong>{current}</strong></p>}
                </div>
              );
            })}
          </div>
          <DialogFooter><DialogClose asChild><Button variant="outline">Fechar</Button></DialogClose></DialogFooter>
        </DialogContent>
      </Dialog>

      <ProfessorFeedback open={isFeedbackDialogOpen} onOpenChange={setIsFeedbackDialogOpen} student={selectedStudent} onSuccess={refreshCurrentPage} lang="pt" />

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="w-12 h-12 border-4 border-[#6697a4] border-t-transparent rounded-full animate-spin"></div>
        </div>
      ) : students.length === 0 ? (
        <Card className="bg-white border-0 shadow-sm rounded-xl">
          <CardContent className="p-12 text-center">
            <Users className="h-12 w-12 text-[#94A3B8] mx-auto mb-4" />
            <p className="text-[#475569]">{searchInput ? "Nenhum aluno encontrado" : "Sem alunos registados"}</p>
          </CardContent>
        </Card>
      ) : (
        <>
          <div className="grid gap-3">
            {students.map((student) => (
              <Card key={student.user_id} className={`bg-white border-0 shadow-sm rounded-xl ${student.blocked ? "opacity-60 ring-2 ring-red-200" : ""}`} data-testid={`student-${student.user_id}`}>
                <CardContent className="p-4 sm:p-5">
                  <div className="flex items-start gap-3 sm:gap-4">
                    <Avatar className="h-10 w-10 sm:h-12 sm:w-12 shrink-0">
                      <AvatarImage src={student.picture} />
                      <AvatarFallback className="bg-[#6697a4] text-white">{student.name?.charAt(0)}</AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Link to={`/admin/students/${student.user_id}`} className="font-medium text-[#3d5a63] hover:text-[#6697a4] underline decoration-[#6697a4]/30 underline-offset-2 decoration-1 truncate" data-testid={`profile-link-${student.user_id}`}>{student.name || student.email}</Link>
                        {student.blocked && <Badge className="bg-red-500 text-white border-0 text-xs">Bloqueado</Badge>}
                        {subscriptionMap[student.user_id] && <Badge className="bg-purple-100 text-purple-700 border-0 text-xs">Mensal</Badge>}
                      </div>
                      <p className="text-xs sm:text-sm text-[#94A3B8] truncate">{student.email}</p>
                      {student.phone && <p className="text-xs text-[#94A3B8] flex items-center gap-1 mt-0.5"><Phone className="h-3 w-3" />{student.phone}</p>}
                      <div className="flex gap-1.5 mt-1 flex-wrap">
                        {student.ranking_surf && <Badge className={`text-xs border ${RANKING_COLORS[student.ranking_surf] || ""}`} data-testid={`badge-surf-${student.user_id}`}>Surf: {student.ranking_surf}</Badge>}
                        {student.ranking_bodyboard && <Badge className={`text-xs border ${RANKING_COLORS[student.ranking_bodyboard] || ""}`} data-testid={`badge-bb-${student.user_id}`}>BB: {student.ranking_bodyboard}</Badge>}
                      </div>
                    </div>
                  </div>
                  {/* Stats + Actions row */}
                  <div className="flex items-center gap-2 mt-3 ml-[52px] sm:ml-[64px] flex-wrap">
                    <div className="flex items-center gap-3 mr-auto">
                      <div className="text-center"><p className="text-lg font-bold text-[#6697a4]">{student.credits || 0}</p><p className="text-[10px] text-[#94A3B8]">Grupo</p></div>
                      <div className="text-center"><p className="text-lg font-bold text-[#e8927c]">{student.private_credits || 0}</p><p className="text-[10px] text-[#94A3B8]">Privados</p></div>
                      <div className="text-center"><p className="text-lg font-bold text-[#3d5a63]">{student.enrollment_count}</p><p className="text-[10px] text-[#94A3B8]">Aulas</p></div>
                    </div>
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <Button size="sm" variant="outline" className="h-7 text-xs px-2" onClick={() => openCreditsDialog(student)} data-testid={`credits-${student.user_id}`}><CreditCard className="h-3.5 w-3.5 sm:mr-1" /><span className="hidden sm:inline">Creditos</span></Button>
                      <Button size="sm" variant="outline" className="h-7 text-xs px-2" onClick={() => openRankingDialog(student)} data-testid={`ranking-${student.user_id}`}><Star className="h-3.5 w-3.5 sm:mr-1" /><span className="hidden sm:inline">Rankings</span></Button>
                      <Button size="sm" variant="outline" className="h-7 text-xs px-2 bg-[#6697a4]/5 border-[#6697a4]/30 text-[#6697a4] hover:bg-[#6697a4]/10" onClick={() => openFeedbackDialog(student)} data-testid={`feedback-btn-${student.user_id}`}><MessageCircle className="h-3.5 w-3.5 sm:mr-1" /><span className="hidden sm:inline">Feedback</span></Button>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild><Button size="sm" variant="outline" className="h-7 w-7 p-0" data-testid={`actions-${student.user_id}`}><UserCog className="h-3.5 w-3.5" /></Button></DropdownMenuTrigger>
                        <DropdownMenuContent>
                          <DropdownMenuItem onClick={() => handleChangeRole(student.user_id, "professor")}><GraduationCap className="mr-2 h-4 w-4" /> Tornar Professor</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleChangeRole(student.user_id, "admin")}><User className="mr-2 h-4 w-4" /> Tornar Admin</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleBlockToggle(student)} data-testid={`block-${student.user_id}`}>{student.blocked ? <><Check className="mr-2 h-4 w-4" /> Desbloquear</> : <><X className="mr-2 h-4 w-4 text-red-500" /> Bloquear</>}</DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleRemoveUser(student)} className="text-red-600" data-testid={`remove-${student.user_id}`}><Trash2 className="mr-2 h-4 w-4" /> Remover</DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Pagination */}
          {pages > 1 && (
            <div className="flex items-center justify-center gap-2 pt-2" data-testid="students-pagination">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => handlePageChange(page - 1)} className="h-8 px-3" data-testid="students-prev-page">
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <div className="flex items-center gap-1">
                {Array.from({ length: Math.min(pages, 5) }, (_, i) => {
                  let p;
                  if (pages <= 5) { p = i + 1; }
                  else if (page <= 3) { p = i + 1; }
                  else if (page >= pages - 2) { p = pages - 4 + i; }
                  else { p = page - 2 + i; }
                  return (
                    <Button key={p} variant={p === page ? "default" : "outline"} size="sm" className={`h-8 w-8 p-0 ${p === page ? "bg-[#6697a4] text-white" : ""}`} onClick={() => handlePageChange(p)}>
                      {p}
                    </Button>
                  );
                })}
              </div>
              <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => handlePageChange(page + 1)} className="h-8 px-3" data-testid="students-next-page">
                <ChevronRight className="h-4 w-4" />
              </Button>
              <span className="text-xs text-[#94A3B8] ml-2 hidden sm:inline">{total} aluno{total !== 1 ? "s" : ""}</span>
            </div>
          )}
        </>
      )}

      {/* Create Student Dialog */}
      <Dialog open={showCreateStudent} onOpenChange={setShowCreateStudent}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Criar Novo Aluno</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-sm">Nome *</Label>
              <Input value={newStudent.name} onChange={(e) => setNewStudent({...newStudent, name: e.target.value})} placeholder="Nome do aluno" data-testid="new-student-name" />
            </div>
            <div>
              <Label className="text-sm">Email (opcional)</Label>
              <Input type="email" value={newStudent.email} onChange={(e) => setNewStudent({...newStudent, email: e.target.value})} placeholder="email@exemplo.com" data-testid="new-student-email" />
            </div>
            <div>
              <Label className="text-sm">Telefone (opcional)</Label>
              <Input value={newStudent.phone} onChange={(e) => setNewStudent({...newStudent, phone: e.target.value})} placeholder="+351 9XX XXX XXX" data-testid="new-student-phone" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateStudent(false)}>Cancelar</Button>
            <Button onClick={handleCreateStudent} className="bg-[#6697a4] hover:bg-[#567f8a]" data-testid="save-new-student-btn">Criar Aluno</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminStudents;
